import React, { useState, useEffect, useMemo } from 'react';
import { Empty, Timeline, Tag, Space } from 'antd';
import { ClockCircleOutlined } from '@ant-design/icons';
import getMovimentacoesProcesso from 'services/processes/movimentacoes';
import { Loader } from 'components/Loader/styles';
import * as S from './styles';

const isMovementRecent = (dateStr) => {
  if (!dateStr) return false;
  const time = new Date(dateStr).getTime();
  if (isNaN(time)) return false;
  const now = Date.now();
  return now - time < 7 * 24 * 60 * 60 * 1000 && now >= time;
};

// "Nova" vem do servidor (chegou depois da última vez que você abriu); sem essa
// informação, vale a regra antiga dos últimos 7 dias
const isNova = (m) => (typeof m?.nova === 'boolean' ? m.nova : isMovementRecent(m?.dataHora));


// Origem do andamento (PDPJ e e-SAJ trazem o texto completo)
const FONTE_MOV = { pdpj: 'PDPJ', esaj: 'e-SAJ', datajud: 'DataJud' }

function ProcessTimeline({ processo, onMovimentacoesUpdated, isActive = false }) {
  const [loading, setLoading] = useState(false);
  const [movimentacoes, setMovimentacoes] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);

  // Função para obter a cor com base no tipo de movimento
  const getColorByType = (movementName) => {
    const name = movementName?.toLowerCase() || '';
    
    if (name.includes('publicação') || name.includes('publicacao')) return '#1890ff';
    if (name.includes('audiência') || name.includes('audiencia')) return '#52c41a';
    if (name.includes('sentença') || name.includes('sentenca')) return '#f5222d';
    if (name.includes('despacho')) return '#fa8c16';
    if (name.includes('juntada')) return '#722ed1';
    if (name.includes('conclus')) return '#13c2c2';
    
    return '#8c8c8c';
  };

  // Movimentações salvas pelo robô da madrugada: abrir a aba não consulta o tribunal
  useEffect(() => {
    if (!isActive || !processo?.id) return undefined;
    let ativo = true;
    setLoading(true);
    getMovimentacoesProcesso(processo.id)
      .then((data) => {
        if (!ativo) return;
        setMovimentacoes(data);
        setLastUpdate(data?.atualizadoEm ? new Date(data.atualizadoEm) : null);
      })
      .catch(() => { /* sem dados: cai no que veio junto com o processo */ })
      .finally(() => ativo && setLoading(false));
    return () => { ativo = false; };
  }, [processo?.id, isActive]);

  const naoSincronizado = movimentacoes && movimentacoes.sincronizado === false;

  // Usar movimentações do estado (atualizadas) ou do processo
  const movements = useMemo(
    () => movimentacoes?.movimentos || processo?.movimentacoes?.movimentos || [],
    [movimentacoes?.movimentos, processo?.movimentacoes?.movimentos]
  );
  const assuntos = movimentacoes?.assuntos || processo?.movimentacoes?.assuntos || [];
  const hasMovements = movements?.length > 0;

  // Ordenar movimentações por data (mais recente primeiro)
  const sortedMovements = useMemo(() => {
    const list = Array.isArray(movements) ? [...movements] : [];
    return list.sort((a, b) => new Date(b.dataHora) - new Date(a.dataHora));
  }, [movements]);

  const recentCount = useMemo(() => sortedMovements.filter(isNova).length, [sortedMovements]);

  const isMobile = typeof window !== 'undefined' ? window.innerWidth < 768 : false;

  return (
    <S.Container>
      {/* Cabeçalho */}
      <div style={{ 
        display: 'flex', 
        flexDirection: isMobile ? 'column' : 'row',
        justifyContent: 'space-between', 
        alignItems: isMobile ? 'flex-start' : 'center',
        gap: isMobile ? '12px' : '0',
        marginBottom: '24px',
        paddingBottom: '16px',
        borderBottom: '2px solid var(--yrm-border)'
      }}>
        <div style={{ width: isMobile ? '100%' : 'auto' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: '8px' }}>
            <h3 style={{ 
              margin: 0, 
              fontSize: isMobile ? '16px' : '18px', 
              fontWeight: 600, 
              color: 'var(--yrm-text)' 
            }}>
              Movimentações Processuais
            </h3>
            {recentCount > 0 && (
              <Tag
                color="blue"
                style={{
                  borderRadius: 12,
                  padding: '2px 10px',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  fontWeight: 600,
                  fontSize: 12,
                }}
              >
                <span
                  style={{
                    width: 7,
                    height: 7,
                    borderRadius: '50%',
                    backgroundColor: '#1677ff',
                    display: 'inline-block',
                    boxShadow: '0 0 0 2px rgba(22, 119, 255, 0.25)',
                  }}
                />
                {recentCount === 1 ? '1 nova movimentação recente' : `${recentCount} novas movimentações recentes`}
              </Tag>
            )}
          </div>
          {lastUpdate && (
            <Space size={4} style={{ fontSize: '13px', color: 'var(--yrm-text-3)', flexWrap: 'wrap' }}>
              <ClockCircleOutlined />
              <span>Atualizado automaticamente em {lastUpdate.toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' })}</span>
            </Space>
          )}
        </div>
      </div>

      {/* Assuntos */}
      {assuntos?.length > 0 && (
        <div style={{ 
          marginBottom: '24px',
          padding: isMobile ? '12px' : '16px',
          background: 'var(--yrm-surface-3)',
          borderRadius: '8px',
          border: '1px solid var(--yrm-border)'
        }}>
          <div style={{ 
            fontSize: isMobile ? '12px' : '13px', 
            fontWeight: 600, 
            color: 'var(--yrm-text-2)', 
            marginBottom: '12px' 
          }}>
            ASSUNTOS
          </div>
          <Space wrap>
            {assuntos.map((assunto, index) => (
              <Tag 
                key={index} 
                color="blue" 
                style={{ 
                  fontSize: isMobile ? '12px' : '13px', 
                  padding: isMobile ? '3px 10px' : '4px 12px' 
                }}
              >
                {assunto.nome}
              </Tag>
            ))}
          </Space>
        </div>
      )}

      {loading && <Loader />}
      
      {!loading && hasMovements && (
        <Timeline mode="left">
          {sortedMovements.map((movement, index) => {
            const isRecentDate = isNova(movement);
            const isFirst = index === 0;
            const color = getColorByType(movement.nome);
            
            return (
              <Timeline.Item
                key={index}
                color={isRecentDate ? '#1677ff' : color}
                dot={
                  isRecentDate ? (
                    <span
                      style={{
                        position: 'relative',
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 14,
                        height: 14,
                      }}
                    >
                      <span
                        style={{
                          position: 'absolute',
                          width: 14,
                          height: 14,
                          borderRadius: '50%',
                          backgroundColor: '#1677ff',
                          opacity: 0.35,
                        }}
                      />
                      <span
                        style={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          backgroundColor: '#1677ff',
                        }}
                      />
                    </span>
                  ) : undefined
                }
                style={{ paddingBottom: isMobile ? '24px' : '32px' }}
              >
                <div
                  style={{
                    background: isRecentDate ? 'var(--yrm-tint-azul)' : 'var(--yrm-surface-2)',
                    border: isRecentDate ? '1px solid #bae0ff' : '1px solid var(--yrm-border)',
                    borderRadius: '8px',
                    padding: isMobile ? '12px' : '14px 16px',
                    boxShadow: isRecentDate ? '0 2px 6px rgba(22, 119, 255, 0.08)' : 'none',
                    transition: 'all 0.2s ease',
                  }}
                >
                  {/* Data e Tags */}
                  <div style={{ marginBottom: '10px' }}>
                    <Space size={8} wrap>
                      <Tag 
                        color={color} 
                        style={{ 
                          margin: 0,
                          fontSize: isMobile ? '12px' : '13px',
                          padding: isMobile ? '3px 10px' : '4px 12px',
                          fontWeight: 500
                        }}
                      >
                        {new Date(movement.dataHora).toLocaleDateString('pt-BR', {
                          day: '2-digit',
                          month: 'short',
                          year: 'numeric'
                        })}
                      </Tag>
                      {isRecentDate && (
                        <Tag 
                          color="blue" 
                          style={{ 
                            margin: 0,
                            fontSize: isMobile ? '11px' : '12px',
                            padding: '2px 8px',
                            borderRadius: '10px',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 5,
                            fontWeight: 600,
                          }}
                        >
                          <span
                            style={{
                              width: 6,
                              height: 6,
                              borderRadius: '50%',
                              backgroundColor: '#1677ff',
                              display: 'inline-block',
                            }}
                          />
                          Nova movimentação
                        </Tag>
                      )}
                      {isFirst && !isRecentDate && (
                        <Tag 
                          color="green" 
                          style={{ 
                            margin: 0,
                            fontSize: isMobile ? '11px' : '12px',
                            padding: '2px 8px'
                          }}
                        >
                          Mais Recente
                        </Tag>
                      )}
                      {/* de onde veio o andamento */}
                      {FONTE_MOV[movement.fonte] && (
                        <Tag bordered={false} style={{ margin: 0, fontSize: 12, color: 'var(--yrm-text-2)' }}>{FONTE_MOV[movement.fonte]}</Tag>
                      )}
                    </Space>
                  </div>

                  {/* Título da movimentação */}
                  <div style={{ 
                    fontSize: isMobile ? '14px' : '15px', 
                    fontWeight: 600, 
                    color: 'var(--yrm-text)',
                    marginBottom: movement.complementosTabelados?.length ? '10px' : '0',
                    lineHeight: 1.5,
                    wordBreak: 'break-word'
                  }}>
                    {movement.nome}
                  </div>

                  {/* Detalhes */}
                  {movement.complementosTabelados && movement.complementosTabelados.length > 0 && (
                    <div style={{
                      marginTop: '10px',
                      padding: isMobile ? '10px' : '12px 14px',
                      backgroundColor: isRecentDate ? 'var(--yrm-surface)' : 'var(--yrm-surface-3)',
                      borderRadius: '6px',
                      border: '1px solid var(--yrm-border)',
                      fontSize: isMobile ? '13px' : '14px'
                    }}>
                      {movement.complementosTabelados.map((complemento, cIdx) => (
                        <div key={cIdx} style={{ 
                          marginBottom: cIdx < movement.complementosTabelados.length - 1 ? '8px' : '0',
                          lineHeight: 1.6,
                          wordBreak: 'break-word'
                        }}>
                          <span style={{ fontWeight: 600, color: 'var(--yrm-text-2)' }}>
                            {complemento.nome}:
                          </span>{' '}
                          <span style={{ color: 'var(--yrm-text)' }}>
                            {complemento.descricao}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </Timeline.Item>
            );
          })}
        </Timeline>
      )}
      
      {!loading && !hasMovements && (
        <Empty 
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={
            <div style={{ color: 'var(--yrm-text-3)', fontSize: '14px' }}>
              {!processo?.NProcesso
                ? 'Informe o número do processo para acompanhar as movimentações'
                : naoSincronizado
                  ? 'As movimentações chegam automaticamente na próxima madrugada.'
                  : 'Nenhuma movimentação encontrada'}
            </div>
          }
          style={{ padding: isMobile ? '40px 0' : '60px 0' }}
        />
      )}
    </S.Container>
  );
}

export default ProcessTimeline;
